import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import StatusTag from "../StatusTag/StatusTag";
import { formatDate } from "../../utils/formatDate";
import { formatPrice } from "../../utils/formatPrice";
import { getShopOrders } from "./Api";
import {
  Container,
  OrdersSection,
  OrdersSectionHeader,
  OrderCardWrapper,
  OrderCardGrid,
  OrderCard,
  OrderHeader,
  OrderDate,
  Item,
  ItemName,
  ItemImage,
  ItemDetails,
  Price,
  TotalPrice,
  DetailsLink,
  ItemSummary,
  FlexWrapper,
} from "./RecentOrders.styled";

const RecentOrders = ({ shopHomepageComponent = false}) => {
  const [recentOrdersData, setRecentOrdersData] = useState([]);
  const [hiddenOrders, setHiddenOrders] = useState(false);

  const mapProductsList = (productList) => {
    return {
      items: productList.map((product) => product).splice(0, 2),
      moreItemsCount: productList.length - 2 > 0 ? productList.length - 2 : 0,
    };
  };

  const reorderHandler = (e, orderId, orderItems) => {
    window.shopUpdateBasketTrigger = e.currentTarget;
    window.ReorderItems(orderId, orderItems);
  };

  const handleReorderCtaAction = orderItems => {
    const allowReorder = orderItems.some(product => product.allowReorder === true);
    const url = orderItems[0].reorderAlternativeUrl;

    if (allowReorder) {
      return {
          reorderButtonIsVisible: true,
          reorderUrl: null
      }
    } else if (orderItems.length === 1 && !allowReorder && url) {
      return {
          reorderButtonIsVisible: false,
          reorderUrl: url
      }
    } else {
      return {
          reorderButtonIsVisible: false,
          reorderUrl: null
      }
    }
  };

  useEffect(() => {
    const offset = 0;
    const limit = 3;

    try {
      getShopOrders({ offset, limit })
        .then((response) => {
          if (response?.data?.orders) {
            setRecentOrdersData(response.data.orders);

            window.dataLayer.push({
              event: "myOrdersShown",
            });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const RecentOrdersComponent = ({ data }) => (
    <OrderCardWrapper hiddenOrders={hiddenOrders}>
      <OrderCardGrid>
        {data.map((order) => {
          const { items, moreItemsCount } = mapProductsList(order.orderItems);
          const { reorderUrl, reorderButtonIsVisible } = handleReorderCtaAction(order.orderItems);

          return (
            <OrderCard key={order.orderId}>
              <OrderHeader>
                <OrderDate>
                  Order date: <strong>{formatDate(order.orderDate)}</strong>
                </OrderDate>
                <StatusTag status={order.orderStatus} />
              </OrderHeader>
              <div>
                {items.map((product) => (
                  <Item key={product.productId}>
                    <ItemImage src={product.imagePath} alt="" />
                    <ItemDetails>
                      <ItemName>{product.productName}</ItemName>
                      <FlexWrapper>
                        <div>
                          <Price>{formatPrice(product.productPrice)}</Price>
                        </div>
                        <span>Qty: {product.quantity}</span>
                      </FlexWrapper>
                    </ItemDetails>
                  </Item>
                ))}
              </div>
              <ItemSummary>
                {!!moreItemsCount && <span>+ {moreItemsCount} more items</span>}
                <TotalPrice>{formatPrice(order.orderTotal)}</TotalPrice>
              </ItemSummary>
              {reorderButtonIsVisible && (
                <button
                  className="bn bn--primary s01__card__btn js-open-basket gtm-cta"
                  onClick={event => reorderHandler(event, order.orderId, order.orderItems)}
                >
                  Order again
                </button>
              )}
              {reorderUrl && (
                <a
                  className="bn bn--primary s01__card__btn gtm-cta"
                  href={reorderUrl}
                >
                  Order again
                </a>
              )}
              {shopHomepageComponent ? (
                <DetailsLink
                  href={`/myAccount/Transactions/shop?orderId=${order.orderId}&page=shop-homepage`}
                  className="gtm-linkclick"
                >
                  View order details
                </DetailsLink>
              ) : (
                <DetailsLink
                  as={Link}
                  to={`/Transactions/shop?orderId=${order.orderId}&overview`}
                  className="gtm-linkclick"
                >
                  View order details
                </DetailsLink>
              )}
            </OrderCard>
          );
        })}
      </OrderCardGrid>
      {shopHomepageComponent ? (
        <a
          className="cta-link gtm-linkclick"
          href="/myAccount/Transactions?product=shop"
        >
          View all my orders
        </a>
      ) : (
        <Link
          to="/Transactions?product=shop"
          className="cta-link gtm-linkclick"
        >
          View all my shop orders
        </Link>
      )}
    </OrderCardWrapper>
  );

  return (
    recentOrdersData?.length > 0 && (
      shopHomepageComponent ? (
        <Container>
          <OrdersSection className="grid-container">
            <OrdersSectionHeader hiddenOrders={hiddenOrders}>
              <h2>My orders</h2>
              <button onClick={() => setHiddenOrders(!hiddenOrders)} className="gtm-linkclick">
                {hiddenOrders ? "View my orders" : "Hide my orders"}
              </button>
            </OrdersSectionHeader>
            <RecentOrdersComponent data={recentOrdersData} />
          </OrdersSection>
        </Container>
      ) : (
        <Container>
          <h5>My shop orders</h5>
          <RecentOrdersComponent data={recentOrdersData} />
        </Container>
      )
    )
  );
};

export default RecentOrders;

RecentOrders.propTypes = {
  shopHomepageComponent: PropTypes.bool
};
